import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Badge,
  Button,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
} from 'react-bootstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import PageSizeSelect from '../../components/common/PageSizeSelect';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import 'react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css';

import PageTitle from '../../components/common/PageTitle';
import AuthImage from '../../components/common/AuthImage';
import useRefreshOnReturn from '../../hooks/useRefreshOnReturn';
import { templateService } from '../../services';
import {
  getEffectiveTemplateSearch,
  getTemplateDesignUrl,
  getTemplateEditPath,
  getTemplateListQueryFromSearch,
  getTemplateListSearchFromQuery,
  getTemplatePreviewUrl,
  TEMPLATE_LIST_DEFAULT_QUERY,
} from '../../utils/templatesList';

const SUPPORTED_SORT_FIELDS = ['tempId', 'name', 'categories', 'createdAt', 'updatedAt'];

const getErrorMessage = error => (
  error?.response?.data?.message
  || error?.error
  || error?.message
  || 'Could not load templates.'
);

const dateFormatter = value => {
  if (!value) return <span className="text-muted">—</span>;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return <span className="text-muted">—</span>;
  return <span title={date.toISOString()}>{date.toLocaleDateString('en-CA')}</span>;
};

const sizeFormatter = size => (
  size && size.width && size.height
    ? `${size.width} × ${size.height}`
    : <span className="text-muted">—</span>
);

const categoriesFormatter = categories => (
  Array.isArray(categories) && categories.length
    ? categories.join(', ')
    : <span className="text-muted">—</span>
);

const tagsFormatter = tags => {
  if (!Array.isArray(tags) || !tags.length) return <span className="text-muted">—</span>;
  const visibleTags = tags.slice(0, 3);
  const remaining = tags.length - visibleTags.length;

  return (
    <div className="templates-management-tags" title={tags.join(', ')}>
      {visibleTags.map(tag => <span className="tableTags" key={tag}>{tag}</span>)}
      {remaining > 0 && <Badge variant="light">+{remaining} more</Badge>}
    </div>
  );
};

const openSafeNewTab = url => {
  const openedWindow = window.open(url, '_blank', 'noopener,noreferrer');
  if (openedWindow) openedWindow.opener = null;
};

const TemplatesList = () => {
  const history = useHistory();
  const location = useLocation();
  const group = useSelector(state => state.authentication.user.group);
  const requestVersion = useRef(0);
  const pendingRequest = useRef(null);
  const mounted = useRef(true);
  const previewSession = useRef(Date.now());
  const [query, setQuery] = useState(() => getTemplateListQueryFromSearch(location.search));
  const queryRef = useRef(query);
  const applyingLocationSearch = useRef(false);
  const [searchInput, setSearchInput] = useState(query.search);
  const [templates, setTemplates] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updateError, setUpdateError] = useState('');
  const [updatingIds, setUpdatingIds] = useState([]);
  const [previewTemplateId, setPreviewTemplateId] = useState(null);
  const [previewVersion, setPreviewVersion] = useState(0);
  queryRef.current = query;

  const loadTemplates = useCallback(async () => {
    const queryKey = JSON.stringify(query);
    if (pendingRequest.current?.queryKey === queryKey) return;
    const currentRequest = requestVersion.current + 1;
    requestVersion.current = currentRequest;
    pendingRequest.current = { queryKey, version: currentRequest };
    setLoading(true);
    setError('');

    try {
      const response = await templateService.findManagement(query);
      if (!mounted.current || requestVersion.current !== currentRequest) return;
      setTemplates(Array.isArray(response.items) ? response.items : []);
      setTotal(Number(response.total || 0));
      setPreviewVersion(current => current + 1);
    } catch (requestError) {
      if (!mounted.current || requestVersion.current !== currentRequest) return;
      setError(getErrorMessage(requestError));
    } finally {
      if (pendingRequest.current?.version === currentRequest) pendingRequest.current = null;
      if (mounted.current && requestVersion.current === currentRequest) setLoading(false);
    }
  }, [query]);

  useRefreshOnReturn(loadTemplates);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      requestVersion.current += 1;
      pendingRequest.current = null;
    };
  }, []);

  useEffect(() => {
    const nextQuery = getTemplateListQueryFromSearch(location.search);
    if (JSON.stringify(queryRef.current) !== JSON.stringify(nextQuery)) {
      applyingLocationSearch.current = true;
      setQuery(nextQuery);
    }
    setSearchInput(current => (current === nextQuery.search ? current : nextQuery.search));
  }, [location.search]);

  useEffect(() => {
    if (applyingLocationSearch.current) {
      applyingLocationSearch.current = false;
      return;
    }

    const nextSearch = getTemplateListSearchFromQuery(query);
    if (nextSearch !== location.search) {
      history.replace({ pathname: location.pathname, search: nextSearch });
    }
  }, [history, location.pathname, location.search, query]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    const effectiveSearch = getEffectiveTemplateSearch(searchInput);
    if (effectiveSearch === null) return undefined;

    const timeout = setTimeout(() => {
      setQuery(current => (
        current.search === effectiveSearch
          ? current
          : { ...current, page: 1, search: effectiveSearch }
      ));
    }, 500);

    return () => clearTimeout(timeout);
  }, [searchInput]);

  const rows = useMemo(() => templates.map(template => ({
    id: template._id,
    tempId: template.tempId,
    name: template.name || '',
    src: getTemplatePreviewUrl(
      process.env.REACT_APP_FILE_SERVER,
      template,
      `${previewSession.current}-${previewVersion}`,
    ),
    size: template.size || {},
    categories: template.categories || [],
    tags: template.tags || [],
    invisible: Boolean(template.invisible),
    price: Number(template.price || 0),
    updatedAt: template.updatedAt,
  })), [previewVersion, templates]);

  const previewTemplate = rows.find(row => row.id === previewTemplateId);

  const updateTemplate = useCallback(async (id, changes) => {
    setUpdatingIds(current => [...current, id]);
    setUpdateError('');
    try {
      const updatedState = await templateService.updateManagementState(id, changes);
      if (!mounted.current) return;
      setTemplates(current => current.map(template => (
        template._id === id
          ? {
            ...template,
            invisible: updatedState.invisible,
            price: updatedState.price,
            updatedAt: updatedState.updatedAt || template.updatedAt,
          }
          : template
      )));
    } catch (requestError) {
      if (mounted.current) setUpdateError(getErrorMessage(requestError));
    } finally {
      if (mounted.current) setUpdatingIds(current => current.filter(item => item !== id));
    }
  }, []);

  const previewFormatter = useCallback((src, row) => (
    <Button
      type="button"
      variant="link"
      className="templates-management-preview-button"
      onClick={() => setPreviewTemplateId(row.id)}
      aria-label={`Preview ${row.name || `template ${row.tempId}`}`}
      title="Open preview"
    >
      <AuthImage src={src} className="tableTempPreview" alt="" />
    </Button>
  ), []);

  const statusFormatter = useCallback((invisible, row) => {
    const updating = updatingIds.includes(row.id);
    const badge = (
      <Badge variant={invisible ? 'danger' : 'success'}>
        {invisible ? 'Unpublished' : 'Published'}
      </Badge>
    );

    if (group !== 'vibino') return badge;
    return (
      <Button
        type="button"
        variant="link"
        className="templates-management-state-button"
        disabled={updating}
        onClick={() => updateTemplate(row.id, { invisible: !invisible })}
        aria-label={`Mark ${row.name || row.tempId} as ${invisible ? 'published' : 'unpublished'}`}
        title={invisible ? 'Publish template' : 'Unpublish template'}
      >
        {updating ? <Spinner animation="border" size="sm" /> : badge}
      </Button>
    );
  }, [group, updateTemplate, updatingIds]);

  const pricingFormatter = useCallback((price, row) => {
    const updating = updatingIds.includes(row.id);
    return (
      <Button
        type="button"
        variant="link"
        className="templates-management-state-button"
        disabled={updating}
        onClick={() => updateTemplate(row.id, { price: price > 0 ? 0 : 1 })}
        aria-label={`Mark ${row.name || row.tempId} as ${price > 0 ? 'free' : 'premium'}`}
        title={price > 0 ? 'Mark as free' : 'Mark as premium'}
      >
        {updating ? (
          <Spinner animation="border" size="sm" />
        ) : (
          <Badge variant={price > 0 ? 'warning' : 'secondary'}>
            {price > 0 ? 'Premium' : 'Free'}
          </Badge>
        )}
      </Button>
    );
  }, [updateTemplate, updatingIds]);

  const actionFormatter = useCallback((id, row) => (
    <div className="templates-management-actions">
      <Button
        type="button"
        size="sm"
        variant="primary"
        onClick={() => openSafeNewTab(
          getTemplateDesignUrl(process.env.REACT_APP_DESIGN_EDITOR, id, row.tempId),
        )}
        title="Open the design editor in a new tab"
      >
        <i className="material-icons">design_services</i> Design
      </Button>
      <Button
        type="button"
        size="sm"
        variant="outline-primary"
        onClick={() => history.push(getTemplateEditPath(id))}
        title="Edit template details"
      >
        <i className="material-icons">edit</i> Edit
      </Button>
    </div>
  ), [history]);

  const columns = useMemo(() => [
    { text: '#', dataField: 'tempId', sort: true },
    { text: 'Preview', dataField: 'src', formatter: previewFormatter },
    { text: 'Name', dataField: 'name', sort: true },
    { text: 'Status', dataField: 'invisible', formatter: statusFormatter },
    { text: 'Pricing', dataField: 'price', formatter: pricingFormatter },
    { text: 'Categories', dataField: 'categories', formatter: categoriesFormatter, sort: true },
    { text: 'Tags', dataField: 'tags', formatter: tagsFormatter },
    { text: 'Size', dataField: 'size', formatter: sizeFormatter },
    { text: 'Updated', dataField: 'updatedAt', formatter: dateFormatter, sort: true },
    { text: 'Action', dataField: 'id', formatter: actionFormatter },
  ], [actionFormatter, previewFormatter, pricingFormatter, statusFormatter]);

  const handleTableChange = useCallback((type, state) => {
    if (type === 'pagination') {
      setQuery(current => ({
        ...current,
        page: state.page,
        limit: state.sizePerPage,
      }));
      return;
    }

    if (type === 'sort' && SUPPORTED_SORT_FIELDS.includes(state.sortField)) {
      setQuery(current => ({
        ...current,
        page: 1,
        sortBy: state.sortField,
        sortOrder: state.sortOrder === 'asc' ? 'asc' : 'desc',
      }));
    }
  }, []);

  const setFilter = (key, value) => {
    setQuery(current => ({ ...current, page: 1, [key]: value }));
  };

  const clearFilters = () => {
    setSearchInput('');
    setQuery(current => ({ ...TEMPLATE_LIST_DEFAULT_QUERY, limit: current.limit }));
  };

  const customTotal = (from, to, size) => (
    <span className="react-bootstrap-table-pagination-total">
      Showing {from} to {to} of {size} Results
    </span>
  );

  const paginationOptions = {
    sizePerPageRenderer: PageSizeSelect,
    paginationSize: 3,
    page: query.page,
    sizePerPage: query.limit,
    totalSize: total,
    hidePageListOnlyOnePage: true,
    showTotal: true,
    paginationTotalRenderer: customTotal,
    sizePerPageList: [
      { text: '10', value: 10 },
      { text: '25', value: 25 },
      { text: '50', value: 50 },
    ],
  };

  const searchLength = searchInput.trim().length;
  const searchTooShort = searchLength > 0 && searchLength < 3;
  const hasFilters = Boolean(query.search || query.status || query.pricing);

  return (
    <Container fluid className="main-content-container px-4 templates-management-page">
      <Row noGutters className="page-header py-4">
        <PageTitle sm="4" title="Templates List" subtitle="Templates" className="text-sm-left" />
        <Button
          type="button"
          variant="outline-primary"
          className="ml-auto align-self-center"
          disabled={loading}
          onClick={loadTemplates}
        >
          Refresh previews
        </Button>
      </Row>

      {error && (
        <Alert variant="danger">
          <span>{error}</span>
          <Button variant="outline-danger" size="sm" className="ml-3" onClick={loadTemplates}>
            Retry
          </Button>
        </Alert>
      )}
      {updateError && <Alert variant="danger">{updateError}</Alert>}

      <div className="templates-management-toolbar">
        <div className="templates-management-toolbar-row">
          <Form.Group className="templates-management-search">
            <Form.Label htmlFor="templates-management-search">Search</Form.Label>
            <Form.Control
              id="templates-management-search"
              type="search"
              value={searchInput}
              onChange={event => setSearchInput(event.target.value)}
              placeholder="ID, name, category or tag"
            />
            <Form.Text className={searchTooShort ? 'text-warning' : 'text-muted'}>
              {searchTooShort
                ? `${3 - searchLength} more character${searchLength === 2 ? '' : 's'} required`
                : 'Type at least 3 characters to search.'}
            </Form.Text>
          </Form.Group>

          <Form.Group>
            <Form.Label htmlFor="templates-status-filter">Status</Form.Label>
            <Form.Control
              id="templates-status-filter"
              as="select"
              value={query.status}
              disabled={loading}
              onChange={event => setFilter('status', event.target.value)}
            >
              <option value="">All statuses</option>
              <option value="published">Published</option>
              <option value="unpublished">Unpublished</option>
            </Form.Control>
          </Form.Group>

          <Form.Group>
            <Form.Label htmlFor="templates-pricing-filter">Pricing</Form.Label>
            <Form.Control
              id="templates-pricing-filter"
              as="select"
              value={query.pricing}
              disabled={loading}
              onChange={event => setFilter('pricing', event.target.value)}
            >
              <option value="">All pricing</option>
              <option value="free">Free</option>
              <option value="premium">Premium</option>
            </Form.Control>
          </Form.Group>

          <Form.Group className="templates-management-clear">
            <Form.Label aria-hidden="true">Actions</Form.Label>
            <Button
              variant="outline-secondary"
              disabled={!hasFilters || loading}
              onClick={clearFilters}
            >
              Clear
            </Button>
          </Form.Group>

          <div className="templates-management-count">
            <Form.Label aria-hidden="true">Results</Form.Label>
            <span aria-live="polite">
              {loading && <Spinner animation="border" size="sm" className="mr-2" />}
              {loading ? 'Updating templates...' : `${total} templates`}
            </span>
          </div>
        </div>
      </div>

      <div className="templates-management-table">
        <BootstrapTable
          key={`${query.sortBy}-${query.sortOrder}`}
          keyField="id"
          data={rows}
          columns={columns}
          remote={{ pagination: true, sort: true, filter: false, cellEdit: false }}
          loading={loading}
          defaultSorted={[{ dataField: query.sortBy, order: query.sortOrder }]}
          pagination={paginationFactory(paginationOptions)}
          onTableChange={handleTableChange}
          noDataIndication={() => (
            loading ? 'Loading templates...' : 'No templates match the selected filters.'
          )}
          bordered={false}
          hover
          wrapperClasses="table-responsive"
        />
      </div>

      <Modal
        show={Boolean(previewTemplate)}
        onHide={() => setPreviewTemplateId(null)}
        centered
        size="lg"
        aria-labelledby="templates-management-preview-title"
      >
        <Modal.Header closeButton>
          <Modal.Title id="templates-management-preview-title">
            {previewTemplate?.name || `Template ${previewTemplate?.tempId || ''}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="templates-management-preview-modal-body">
          {previewTemplate && (
            <AuthImage
              src={previewTemplate.src}
              className="templates-management-preview-image"
              alt={previewTemplate.name || `Template ${previewTemplate.tempId}`}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setPreviewTemplateId(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default TemplatesList;
